import React, { useMemo, useRef, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Form, Formik, FormikProps } from "formik";
import {
  CarrierAssignmentChangeLogFilterModel,
  CarrierAssignmentChangeLogFilterSchema,
  CarrierAssignmentChangeLogItem,
} from "app/data/carrier-assignment-changes/models";
import { useCarrierAssignmentChangeLog } from "../hooks/useCarrierAssignmentChangeLog";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "ui-components/button/button";
import Table from "ui-components/table/table";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import "./carrierChangeHistory.scss";

type CarrierChangeHistoryProps = {
  planId?: string | number | null;
  enabled?: boolean;
};

type CarrierChangeHistoryRow = {
  changedAt: string;
  modifiedBy: string;
  previousCarrier: string;
  newCarrier: string;
  reason: string;
};

const initialValues: CarrierAssignmentChangeLogFilterModel = {
  dateFrom: undefined,
  dateTo: undefined,
};

const formatCarrierLabel = (
  name?: string | null,
  number?: string | number | null
): string => {
  if (name && number != null && number !== "") return `${name} (${number})`;
  if (name) return name;
  if (number != null && number !== "") return String(number);
  return "—";
};

const columnHelper = createColumnHelper<CarrierChangeHistoryRow>();

const columns = [
  columnHelper.accessor("changedAt", {
    id: "changedAt",
    header: "Timestamp",
    cell: (cellProps) => {
      const value = cellProps.getValue();
      return (
        <>
          {value && (
            <div className="xgs-carrier-change-history__date">
              <div>{(value as any).toUiDateFormat?.() ?? value}</div>
              <div className="xgs-carrier-change-history__gray-value">
                {(value as any).toUiTimeShortFormat?.() ?? ""}
              </div>
            </div>
          )}
        </>
      );
    },
    size: 140,
    minSize: 120,
  }),
  columnHelper.accessor("modifiedBy", {
    id: "modifiedBy",
    header: "User",
    size: 100,
    minSize: 80,
  }),
  columnHelper.accessor("previousCarrier", {
    id: "previousCarrier",
    header: "Previous Carrier",
    size: 180,
    minSize: 120,
  }),
  columnHelper.accessor("newCarrier", {
    id: "newCarrier",
    header: "New Carrier",
    size: 180,
    minSize: 120,
  }),
  columnHelper.accessor("reason", {
    id: "reason",
    header: "Reason",
    size: 180,
    minSize: 120,
  }),
];

const mapToRows = (items: CarrierAssignmentChangeLogItem[]): CarrierChangeHistoryRow[] =>
  items.map((item) => ({
    changedAt: item.changedAt,
    modifiedBy: item.modifiedBy,
    previousCarrier: formatCarrierLabel(item.previousCarrierName, item.previousCarrierNumber),
    newCarrier: formatCarrierLabel(item.newCarrierName, item.newCarrierNumber),
    reason: item.reasonDescription || item.reasonCode || "—",
  }));

/**
 * Story 1 — read-only carrier assignment change history for a Static Load lane.
 * GET /carrier-assignment-changes/by-lane/{planId}
 */
const CarrierChangeHistory: React.FC<CarrierChangeHistoryProps> = ({
  planId,
  enabled = true,
}) => {
  const filterFormRef = useRef<FormikProps<CarrierAssignmentChangeLogFilterModel>>(null);
  const [filter, setFilter] = useState<CarrierAssignmentChangeLogFilterModel>(initialValues);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
  } = useCarrierAssignmentChangeLog(planId, filter, enabled && !!planId);

  const rows = useMemo(() => {
    const items = data?.pages.flatMap((page) => page.content) ?? [];
    return mapToRows(items);
  }, [data]);

  const onFilterSubmit = (formData: CarrierAssignmentChangeLogFilterModel) => {
    setFilter({
      dateFrom: formData.dateFrom ? (formData.dateFrom as any).toApiDateFormat?.() ?? formData.dateFrom : undefined,
      dateTo: formData.dateTo ? (formData.dateTo as any).toApiDateFormat?.() ?? formData.dateTo : undefined,
    });
  };

  if (!planId) return null;

  return (
    <div className="xgs-carrier-change-history">
      <div className="xgs-carrier-change-history__header">Carrier Change History</div>

      <div className="xgs-carrier-change-history__grid">
        <Formik
          initialValues={initialValues}
          onSubmit={onFilterSubmit}
          validationSchema={CarrierAssignmentChangeLogFilterSchema}
          innerRef={filterFormRef}
        >
          {(props: FormikProps<CarrierAssignmentChangeLogFilterModel>) => (
            <Form>
              <div className="xgs-carrier-change-history__controls__search">
                <LabeledDateRangeInput
                  label="Filter by Date Range:"
                  labelMode={LabelModes.column}
                  start={props.values.dateFrom}
                  end={props.values.dateTo}
                  maxDate={new Date()}
                  onStartChange={(v) => {
                    props.setFieldValue("dateFrom", v || undefined);
                    props.submitForm();
                  }}
                  onEndChange={(v) => {
                    props.setFieldValue("dateTo", v || undefined);
                    props.submitForm();
                  }}
                />
                <Button
                  type="submit"
                  theme={ButtonThemes.blue}
                  className="xgs-carrier-change-history__controls__button"
                >
                  Search
                </Button>
                <Button
                  type="button"
                  theme={ButtonThemes.gray}
                  className="xgs-carrier-change-history__controls__button"
                  disabled={!props.values.dateFrom && !props.values.dateTo}
                  onClick={() => {
                    props.resetForm();
                    setFilter(initialValues);
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <div className="xgs-carrier-change-history__table-wrapper">
        {!isError && (
          <Table
            isLoading={isLoading}
            columns={columns}
            data={rows}
            rowHeight={48}
            infiniteScroll={true}
            noResultsText="No carrier changes found for this lane."
            infiniteScrollLoading={isFetchingNextPage}
            infiniteScrollHasNext={!!hasNextPage}
            onInfiniteScroll={() => {
              if (hasNextPage) fetchNextPage();
            }}
          />
        )}
      </div>

      {isError && (
        <XGSErrorMessage className="xgs-carrier-change-history__error">
          Error while obtaining carrier change history
        </XGSErrorMessage>
      )}
    </div>
  );
};

export default CarrierChangeHistory;
